/**
 * 1.5.17 §4b — the part of the screen a person can really see. On an iPhone the on-screen keyboard does not
 * shrink the page (the layout viewport): it covers it, and only `window.visualViewport` says how much is left.
 * While `el` is on screen this keeps two CSS variables on it — `--vv-height` (the visible height) and
 * `--kb-inset` (what the keyboard covers at the bottom, never negative) — and `data-kb="open"` while the
 * keyboard is up, so a stylesheet can lay a bottom sheet out above the keyboard. Where the browser has no
 * visual viewport nothing is set and the stylesheet's fallbacks apply.
 */
import { useEffect } from 'react';

/** A keyboard is at least this tall; anything less is a toolbar or a rounding. */
export const KEYBOARD_MIN = 60;

export function readVisualViewport(win: Window = window): { height: number; inset: number } | null {
  const vv = win.visualViewport;
  if (!vv) return null;
  // the layout viewport's height: fixed elements are placed against it, keyboard or not
  const layout = Math.max(win.innerHeight, win.document.documentElement.clientHeight);
  return { height: Math.round(vv.height), inset: Math.max(0, Math.round(layout - vv.height - vv.offsetTop)) };
}

/** Under this many pixels a change is a rounding or a rubber band, not a keyboard: nothing is re-laid out for it. */
const DEAF_UNDER = 2;

/**
 * 1.5.19 §4 — the rule: **a scroll or viewport event handler never scrolls anything.** On an iPhone
 * `scrollIntoView` moves the visual viewport, which fires `visualViewport.scroll`; a handler that scrolls
 * answers its own event for ever, and the page slides off the screen. So this hook has no callback: it only
 * writes the two variables. The safety net: any number of `resize`/`scroll` events become one write per
 * animation frame, and a change of under 2 px writes nothing.
 */
export function useVisualViewport(el: HTMLElement | null): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!el || !vv) return;
    let last: { height: number; inset: number } | null = null;
    let frame = 0;
    const apply = () => {
      frame = 0;
      const now = readVisualViewport();
      if (!now) return;
      if (last && Math.abs(now.height - last.height) < DEAF_UNDER && Math.abs(now.inset - last.inset) < DEAF_UNDER) return;
      last = now;
      el.style.setProperty('--vv-height', `${now.height}px`);
      el.style.setProperty('--kb-inset', `${now.inset}px`);
      if (now.inset >= KEYBOARD_MIN) el.setAttribute('data-kb', 'open');
      else el.removeAttribute('data-kb');
    };
    const later = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };
    apply();
    vv.addEventListener('resize', later);
    vv.addEventListener('scroll', later);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      vv.removeEventListener('resize', later);
      vv.removeEventListener('scroll', later);
    };
  }, [el]);
}
